/**
 * Calendar API Response Normalization Utility
 * 
 * Purpose:
 * Pure function for normalizing API response payloads that may arrive in different
 * formats (wrapped with metadata vs flat payloads). Ensures consistent data shape
 * for downstream processing.
 * 
 * High-level Workflow:
 * 1. Check if response is null/undefined - return null
 * 2. Check if response has a .data property (wrapped format) - extract and return .data
 * 3. If response is flat, strip metadata fields (outcome, data, message, requestId, etc.)
 *    and return the remaining payload
 * 4. If no data fields remain after stripping metadata, return null
 * 
 * Assumptions & Invariants:
 * - API responses may be in one of two formats:
 *   - Wrapped: { outcome: 'success', data: { ...actual data... }, message: '...', requestId: '...' }
 *   - Flat: { ...actual data..., outcome: 'success', message: '...', requestId: '...' }
 * - Metadata fields to strip: outcome, data, message, requestId, metadata, errorCode, status
 * - If response.data exists and is an object, it is the wrapped format
 * - If response.data does not exist or is not an object, treat as flat format
 * - Returns null if no actual data fields remain after normalization
 * - This function does NOT validate data structure - only normalizes format
 * 
 * Dependencies:
 * - Internal: None (pure function, no dependencies)
 * - External: None (uses only JavaScript built-in object operations)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 1 refactoring)
 */

/**
 * Normalize an API response payload to a consistent format
 * 
 * API responses can arrive in two formats:
 * 1. Wrapped: { data: { ...actual data... }, outcome: 'success', ... }
 * 2. Flat: { ...actual data..., outcome: 'success', ... }
 * 
 * This function extracts the actual data payload regardless of format,
 * stripping out metadata fields like outcome, message, requestId, etc.
 * 
 * @param {Object|null|undefined} response - API response object (may be wrapped or flat)
 * @returns {Object|null} Normalized payload with only data fields, or null if no data
 */
export function normalizeApiPayload(response) {
  if (!response) return null;
  
  // If response has .data, extract it (wrapped format)
  if (response.data && typeof response.data === 'object') {
    return response.data;
  }
  
  // If response is already flat payload, strip metadata fields and return
  const { outcome, data, message, requestId, metadata, errorCode, status, ...payload } = response;
  
  // Return payload if it has actual data fields, otherwise return null
  return Object.keys(payload).length > 0 ? payload : null;
}
